import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { Store, Plus, Layers } from "lucide-react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ShopListingDto } from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { fmtDate, num, rm } from "../lib/format";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { useBottomBarActions } from "../hooks/useBottomBarActions";
import { useInfiniteCatalogProducts } from "../hooks/useInfiniteCatalogProducts";
import { Badge, Button, Input, SearchSelect, Textarea, Toggle, statusColor, useConfirm } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { flattenInfinitePages, getNextPageParam } from "../lib/infinite-list";
import { showQueryLoader } from "../lib/query-utils";
import { RightSidebarPanel } from "../components/RightSidebarPanel";
import { ImageHoverPreview } from "../components/ImageHoverPreview";
import { ProductImpressionTrendPopover } from "../components/catalog/ProductImpressionTrendPopover";

function listingThumbnail(row: ListingRow): string | null {
  const product = row.product;
  if (!product) return null;
  return product.displayImages?.[0] ?? product.images?.[0] ?? null;
}

interface ListingRow extends ShopListingDto {
  productSku?: string;
  productName?: string;
  stockAvailable?: number;
  categoryName?: string | null;
  impressions7d?: { listing: number; detail: number };
  wishlistCount?: number;
}

interface ListingForm {
  productId: string;
  active: boolean;
  quantity: string;
  title: string;
  description: string;
  price: string;
  compareAtPrice: string;
  sortOrder: string;
  featured: boolean;
}

const PAGE_SIZE = 25;

function emptyForm(): ListingForm {
  return {
    productId: "",
    active: false,
    quantity: "1",
    title: "",
    description: "",
    price: "",
    compareAtPrice: "",
    sortOrder: "0",
    featured: false,
  };
}

function rowToForm(row: ListingRow): ListingForm {
  return {
    productId: row.productId,
    active: row.active,
    quantity: String(row.quantity),
    title: row.title ?? "",
    description: row.description ?? "",
    price: row.price != null ? String(row.price) : "",
    compareAtPrice: row.compareAtPrice != null ? String(row.compareAtPrice) : "",
    sortOrder: String(row.sortOrder),
    featured: row.featured,
  };
}

function formToBody(form: ListingForm, isCreate: boolean) {
  const quantity = Number.parseInt(form.quantity, 10);
  const body: Record<string, unknown> = {
    active: form.active,
    quantity: Number.isFinite(quantity) && quantity >= 1 ? quantity : 1,
    title: form.title.trim() || null,
    description: form.description.trim() || null,
    price: form.price.trim() ? Number.parseFloat(form.price) : null,
    compareAtPrice: form.compareAtPrice.trim() ? Number.parseFloat(form.compareAtPrice) : null,
    sortOrder: Number.parseInt(form.sortOrder, 10) || 0,
    featured: form.featured,
  };
  if (isCreate) body.productId = form.productId;
  return body;
}

export function ShopListings() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { confirm, dialog } = useConfirm();
  const canWrite = can("catalog.write");

  const [search, setSearch] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ListingRow | null>(null);
  const [form, setForm] = useState<ListingForm>(emptyForm());
  const [productSearch, setProductSearch] = useState("");
  const [trendProduct, setTrendProduct] = useState<{ id: string; name: string } | null>(null);
  const [debouncedProductSearch, setDebouncedProductSearch] = useState("");

  useEffect(() => {
    const t = globalThis.setTimeout(() => setDebouncedProductSearch(productSearch.trim()), 250);
    return () => globalThis.clearTimeout(t);
  }, [productSearch]);

  const listQuery = useInfiniteQuery({
    queryKey: ["shop-listings", search],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api<{ items: ListingRow[]; total: number }>(
        `/catalog/shop-listings?page=${pageParam}&pageSize=${PAGE_SIZE}${search ? `&search=${encodeURIComponent(search)}` : ""}`,
      ),
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(
        lastPage.total,
        allPages.reduce((acc, page) => acc + page.items.length, 0),
        PAGE_SIZE,
      ),
  });

  const listTotal = listQuery.data?.pages[0]?.total ?? 0;

  const warehouseQuery = useInfiniteCatalogProducts({
    enabled: editorOpen && !editing && canWrite,
    search: debouncedProductSearch,
    queryKey: ["warehouse-unlisted"],
    params: {
      withoutShopListing: "true",
      excludeCreditPacks: "true",
    },
  });

  const rows = useMemo(
    () =>
      flattenInfinitePages(listQuery.data?.pages).map((item) => ({
        ...item,
        productSku: item.product?.sku,
        productName: item.product?.name,
        stockAvailable: item.product?.stockAvailable,
        categoryName: item.product?.categoryName,
      })),
    [listQuery.data?.pages],
  );

  const hasNextPage =
    rows.length < listTotal && (listQuery.hasNextPage ?? false);

  const productOptions = useMemo(
    () =>
      warehouseQuery.items.map((p) => ({
        value: p.id,
        label: p.name,
        hint: `${p.sku ?? "—"} · ${num(p.stockAvailable ?? 0)} in WH`,
        image: p.images?.[0] ?? null,
      })),
    [warehouseQuery.items],
  );

  const selectedWarehouseStock = useMemo(() => {
    if (editing?.product?.stockAvailable != null) return editing.product.stockAvailable;
    const picked = warehouseQuery.items.find((p) => p.id === form.productId);
    return picked?.stockAvailable;
  }, [editing, warehouseQuery.items, form.productId]);

  const maxListQuantity = useMemo(() => {
    if (editing) {
      const wh = editing.product?.stockAvailable ?? 0;
      const unsold = Math.max(0, editing.quantity - editing.soldCount);
      return wh + unsold;
    }
    return selectedWarehouseStock ?? undefined;
  }, [editing, selectedWarehouseStock]);

  const saveMutation = useMutation({
    mutationFn: (payload: { id?: string; body: Record<string, unknown> }) => {
      if (payload.id) {
        return api(`/catalog/shop-listings/${payload.id}`, { method: "PUT", body: payload.body });
      }
      return api("/catalog/shop-listings", { method: "POST", body: payload.body });
    },
    onSuccess: () => {
      toast("success", editing ? "Listing updated" : "Listing created");
      closeEditor();
      void queryClient.invalidateQueries({ queryKey: ["shop-listings"] });
      void queryClient.invalidateQueries({ queryKey: ["catalog-products-infinite"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Save failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      api<{ product?: { name: string; sku: string } }>(`/catalog/shop-listings/${id}`, { method: "DELETE" }),
    onSuccess: (data) => {
      const label = data.product?.sku ?? data.product?.name;
      toast("success", label ? `Listing removed — ${label} stays in warehouse` : "Listing removed — warehouse product unchanged");
      closeEditor();
      void queryClient.invalidateQueries({ queryKey: ["shop-listings"] });
      void queryClient.invalidateQueries({ queryKey: ["catalog-products"] });
      void queryClient.invalidateQueries({ queryKey: ["catalog-products-infinite"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Delete failed"),
  });

  function closeEditor() {
    setEditorOpen(false);
    setEditing(null);
    setForm(emptyForm());
    setProductSearch("");
    setDebouncedProductSearch("");
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setProductSearch("");
    setDebouncedProductSearch("");
    setEditorOpen(true);
  }

  function openEdit(row: ListingRow) {
    setEditing(row);
    setForm(rowToForm(row));
    setEditorOpen(true);
  }

  useBottomBarActions(() =>
    canWrite
      ? [
          {
            id: "create-shop-listing",
            label: "Create",
            icon: Plus,
            onClick: openCreate,
          },
        ]
      : [],
  );

  const columns: Column<ListingRow>[] = useMemo(
    () => [
      {
        key: "product",
        header: "Product",
        sortable: true,
        sortValue: (r) => r.productName ?? "",
        render: (r) => {
          const thumb = listingThumbnail(r);
          return (
            <div className="flex items-center gap-2.5">
              {thumb ? (
                <ImageHoverPreview src={thumb} previewWidth={240} previewHeight={240}>
                  <img
                    src={thumb}
                    alt=""
                    className="h-9 w-9 shrink-0 rounded-lg border border-line object-cover"
                    style={r.product?.imageBackgroundColor ? { backgroundColor: r.product.imageBackgroundColor } : undefined}
                  />
                </ImageHoverPreview>
              ) : (
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/5 text-white/50">
                  <Layers size={16} />
                </div>
              )}
              <div className="min-w-0">
                <p className="truncate font-medium text-white/90">{r.title || r.productName}</p>
                <p className="font-mono text-[10px] text-white/40">{r.productSku}</p>
              </div>
            </div>
          );
        },
      },
      {
        key: "category",
        header: "Category",
        render: (r) => <span className="text-xs text-white/60">{r.categoryName ?? "—"}</span>,
      },
      {
        key: "impressions",
        header: "Impressions (7d)",
        className: "text-right",
        hideSm: true,
        sortValue: (r) => (r.impressions7d?.listing ?? 0) + (r.impressions7d?.detail ?? 0),
        render: (r) => {
          const imp = r.impressions7d;
          const total = (imp?.listing ?? 0) + (imp?.detail ?? 0);
          return (
            <button
              type="button"
              className="text-right text-xs hover:text-brand-pink"
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                setTrendProduct({
                  id: r.productId,
                  name: r.title || r.productName || "Product",
                });
              }}
            >
              <span className="font-medium tabular-nums">{num(total)}</span>
              <span className="block text-[10px] text-muted">
                {imp ? `${imp.listing} list · ${imp.detail} detail` : "—"}
              </span>
            </button>
          );
        },
      },
      {
        key: "wishlist",
        header: "Wishlisted",
        className: "text-right",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.wishlistCount ?? 0,
        exportValue: (r) => r.wishlistCount ?? 0,
        render: (r) => <span className="text-xs tabular-nums">{num(r.wishlistCount ?? 0)}</span>,
      },
      {
        key: "price",
        header: "Shop price",
        className: "text-right",
        render: (r) => (
          <span className="text-xs">{r.price != null ? rm(r.price) : r.product?.price != null ? rm(r.product.price) : "—"}</span>
        ),
      },
      {
        key: "listed",
        header: "Listed",
        className: "text-right",
        sortValue: (r) => r.quantity,
        render: (r) => (
          <span className="text-xs">
            {num(r.soldCount)}/{num(r.quantity)}
          </span>
        ),
      },
      {
        key: "shopStock",
        header: "Shop avail.",
        className: "text-right",
        sortValue: (r) => r.shopStockAvailable ?? 0,
        render: (r) => <span className="text-xs">{num(r.shopStockAvailable ?? 0)}</span>,
      },
      {
        key: "whStock",
        header: "WH free",
        className: "text-right",
        hideSm: true,
        sortValue: (r) => r.stockAvailable ?? 0,
        render: (r) => <span className="text-xs text-white/50">{num(r.stockAvailable ?? 0)}</span>,
      },
      {
        key: "active",
        header: "Listed",
        sortValue: (r) => r.active,
        render: (r) => (
          <Badge color={r.active ? statusColor("PAID") : statusColor("CANCELLED")}>{r.active ? "Active" : "Hidden"}</Badge>
        ),
      },
      {
        key: "featured",
        header: "Featured",
        render: (r) => (r.featured ? <Badge color="blue">Featured</Badge> : <span className="text-xs text-white/30">—</span>),
      },
      {
        key: "updated",
        header: "Updated",
        hideSm: true,
        render: (r) => <span className="text-xs text-white/50">{fmtDate(r.updatedAt)}</span>,
      },
    ],
    [],
  );

  return (
    <ListPageShell>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={showQueryLoader(listQuery)}
        exportFilename="shop-listings"
        entityLabel="Listing"
        onRowClick={canWrite ? openEdit : undefined}
        activeKey={editing?.id}
        infiniteScroll={{
          hasNextPage,
          isFetchingNextPage: listQuery.isFetchingNextPage,
          onLoadMore: () => void listQuery.fetchNextPage(),
        }}
        rowActions={
          canWrite
            ? {
                onEdit: openEdit,
                onDelete: (row) =>
                  confirm({
                    title: "Remove from shop?",
                    message: `This hides ${row.product?.name ?? row.productName ?? "this item"} from the storefront only. The warehouse product (stock, raffles, packs) is not deleted.`,
                    confirmLabel: "Remove listing",
                    danger: true,
                    action: async () => deleteMutation.mutateAsync(row.id),
                  }),
              }
            : undefined
        }
        toolbar={{
          search: {
            value: search,
            onChange: setSearch,
            onSubmit: () => setSearch(search.trim()),
            placeholder: "Search listings…",
          },
          trailing: canWrite ? (
            <Button size="sm" onClick={openCreate}>
              Create
            </Button>
          ) : undefined,
          onRefresh: () => void listQuery.refetch(),
        }}
        empty={{
          icon: Store,
          title: "No shop listings yet",
          hint: "Pick warehouse products to sell on the storefront.",
          action: canWrite ? (
            <Button size="sm" onClick={openCreate}>
              Create
            </Button>
          ) : undefined,
        }}
      />

      <RightSidebarPanel
        open={editorOpen}
        title={editing ? "Edit shop listing" : "Create shop listing"}
        subtitle={editing ? editing.productSku : undefined}
        onClose={closeEditor}
        footer={
          canWrite && (
            <div className="flex flex-wrap justify-end gap-2">
              {editing && (
                <Button
                  variant="danger"
                  size="sm"
                  disabled={deleteMutation.isPending}
                  onClick={() =>
                    confirm({
                      title: "Remove listing?",
                      message: "The warehouse product stays — only the shop listing is removed.",
                      confirmLabel: "Remove",
                      danger: true,
                      action: async () => deleteMutation.mutateAsync(editing.id),
                    })
                  }
                >
                  Remove
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={closeEditor}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={saveMutation.isPending || (!editing && !form.productId)}
                onClick={() =>
                  saveMutation.mutate({
                    id: editing?.id,
                    body: formToBody(form, !editing),
                  })
                }
              >
                Save
              </Button>
            </div>
          )
        }
      >
        <div className="sidebar-form">
          {!editing && (
            <>
              <SearchSelect
                label="Warehouse product"
                placeholder={warehouseQuery.isLoading ? "Loading products…" : "Select product"}
                searchPlaceholder="Search name or SKU…"
                options={productOptions}
                value={form.productId}
                onChange={(v) => setForm((f) => ({ ...f, productId: v }))}
                onSearchChange={setProductSearch}
                serverFiltered
                hasMore={warehouseQuery.hasNextPage}
                loadingMore={warehouseQuery.isFetchingNextPage}
                onLoadMore={warehouseQuery.fetchNextPage}
                disabled={warehouseQuery.isLoading}
                emptyMessage={
                  warehouseQuery.isError
                    ? "Couldn't load warehouse products"
                    : debouncedProductSearch
                      ? "No matching unlisted products"
                      : "No unlisted warehouse products"
                }
              />
              {!warehouseQuery.isLoading && (
                <p className="text-xs text-white/45">
                  Only warehouse products without a shop listing
                  {warehouseQuery.total > 0
                    ? ` · showing ${productOptions.length} of ${warehouseQuery.total}`
                    : ""}
                  . Scroll or search for more.
                </p>
              )}
            </>
          )}
          {editing?.product && (
            <div className="rounded-xl border border-line bg-white/[0.02] px-3 py-2 text-xs">
              <p className="text-white/40">Warehouse product</p>
              <p className="font-medium text-white/90">{editing.product.name}</p>
              <p className="text-white/50">
                Base price {rm(editing.product.price)} · WH free {num(editing.product.stockAvailable ?? 0)} · Sold{" "}
                {num(editing.soldCount)}
              </p>
            </div>
          )}
          <Input
            label="Shop quantity"
            type="number"
            min={editing ? editing.soldCount : 1}
            max={maxListQuantity}
            step="1"
            hint={
              maxListQuantity != null
                ? editing
                  ? `Up to ${num(maxListQuantity)} (${num(editing?.soldCount ?? 0)} already sold). Rest stays warehouse-only.`
                  : selectedWarehouseStock != null
                    ? `Up to ${num(maxListQuantity)} from warehouse. Rest stays warehouse-only.`
                    : "Pick a product to see warehouse availability"
                : "Units offered in the shop — remainder stays in warehouse"
            }
            value={form.quantity}
            onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
          />
          <Input
            label="Shop title"
            hint="Leave empty to use warehouse name"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />
          <Textarea
            label="Shop description"
            rows={3}
            hint="Leave empty to use warehouse description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <div className="form-row-2">
            <Input
              label="Shop price (MYR)"
              type="number"
              min="0"
              step="0.01"
              hint="Empty = warehouse price"
              value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
            />
            <Input
              label="Compare-at price"
              type="number"
              min="0"
              step="0.01"
              value={form.compareAtPrice}
              onChange={(e) => setForm((f) => ({ ...f, compareAtPrice: e.target.value }))}
            />
          </div>
          <Input
            label="Sort order"
            type="number"
            value={form.sortOrder}
            onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
          />
          <Toggle checked={form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} label="Listed in shop" />
          <Toggle checked={form.featured} onChange={(v) => setForm((f) => ({ ...f, featured: v }))} label="Featured" />
        </div>
      </RightSidebarPanel>

      {trendProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onClick={() => setTrendProduct(null)}
        >
          <div
            className="rounded-2xl border border-line bg-surface p-4 shadow-xl"
            role="dialog"
            aria-label="Impression trend"
            onClick={(e) => e.stopPropagation()}
          >
            <ProductImpressionTrendPopover productId={trendProduct.id} productName={trendProduct.name} />
            <div className="mt-3 flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => setTrendProduct(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {dialog}
    </ListPageShell>
  );
}
